import React, { useState } from "react";

import { signupUser } from "services";

import { useSignupAttribution, useTranslation } from "utils";

import { SOILSIGHT_SIGNUP_ORIGIN } from "consts";

import { Button, Form, IllustrationCard, Input, Link, Svg, Typography } from "components";

import { SignupCheckboxValues, SignupCheckboxes, useSignupCheckboxesAccepted } from "pages/app/account/components/SignupCheckboxes.tsx";
import { trackSoilSightSignupSubmit } from "pages/app/soilsight-pre-login/utils/acquisitionTracking";

interface SignupStepProps {
  onLoginClick: () => void;
}

interface SignupStepFormValues extends SignupCheckboxValues {
  email: string;
}

export const SignupStep = ({ onLoginClick }: SignupStepProps) => {
  const { t } = useTranslation(["scenarioAnalysis", "signinup"]);
  const attribution = useSignupAttribution(SOILSIGHT_SIGNUP_ORIGIN);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedValues, setSubmittedValues] = useState<SignupStepFormValues | null>(null);
  const [form] = Form.useForm<SignupStepFormValues>();
  const checkboxesAccepted = useSignupCheckboxesAccepted(form);

  // `track` is only true for the original submission — a resend re-sends the same already-recorded
  // email, so it must not count as a second attempt (success or fail) in the funnel data.
  const submitSignup = (values: SignupStepFormValues, track = false) => {
    setIsSubmitting(true);
    const controller = new AbortController();

    signupUser({ ...attribution, ...values, signal: controller.signal })
      .then(() => {
        if (track) trackSoilSightSignupSubmit(values.email, true);
        setSubmittedValues(values);
      })
      .catch(() => {
        if (track) trackSoilSightSignupSubmit(values.email, false);
        // The global axios handler already showed the failure. We catch so the rejection does not escape.
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSubmit = (values: SignupStepFormValues) => submitSignup(values, true);

  if (submittedValues) {
    return (
      <div className="soilsight-acquisition-signup soilsight-acquisition-signup--confirm" data-testid="soilsight-step-signup">
        <Typography variant="h1" className="soilsight-acquisition-signup__title">
          {t("signinup:signedUp.title")}
        </Typography>
        <IllustrationCard>
          <Svg svgName="misc/klim_message" />
        </IllustrationCard>
        <Typography variant="paragraph-m">{t("signinup:signedUp.description")}</Typography>
        <Link disabled={isSubmitting} onClick={() => submitSignup(submittedValues)}>
          {t("signinup:signedUp.link")}
        </Link>
      </div>
    );
  }

  return (
    <div className="soilsight-acquisition-signup" data-testid="soilsight-step-signup">
      <Typography variant="h1" className="soilsight-acquisition-signup__title">
        {t("preLogin.simulation.signup.title")}
      </Typography>

      <Form form={form} name="soilsight-signup" className="soilsight-acquisition-signup__form" onFinish={handleSubmit}>
        <Input
          dataTestId="soilsight-signup-email"
          name="email"
          label={t("signinup:signup.inputs.email")}
          rules={[
            {
              required: true,
              type: "email",
            },
          ]}
        />
        <SignupCheckboxes />
        <Button type="primary" htmlType="submit" dataTestId="soilsight-signup-submit" disabled={isSubmitting || !checkboxesAccepted} loading={isSubmitting}>
          {t("preLogin.simulation.signup.submit")}
        </Button>
        <Button type="secondary" dataTestId="soilsight-signup-login" disabled={isSubmitting} onClick={onLoginClick}>
          {t("preLogin.simulation.signup.login")}
        </Button>
      </Form>
    </div>
  );
};
